import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type Repub, REPUBS } from '@cx-modules/constants';

import Icon from '../../../primitives/icon';
import { type TabElement, type TabName, tabStyles } from '../Tabs';

export const useTabs = ({
  DISABLE_GRID_TAB_STATS,
  projectionsUrl,
  repub,
}: {
  DISABLE_GRID_TAB_STATS: boolean;
  projectionsUrl: string | undefined;
  repub: Repub;
}) => {
  const { t } = useTranslation('label');
  const shouldDisableStatsTab = () => {
    // Temp logic to disable stats if not US page
    const repubNotUS = repub !== REPUBS.US.name;

    switch (true) {
      case DISABLE_GRID_TAB_STATS:
        return true;
      case !projectionsUrl:
        return true;
      case repubNotUS:
        return true;
      default:
        return false;
    }
  };

  const OFFERS = t('CX_OFFERS') as TabName;
  const BEST_ODDS = t('CX_BEST_ODDS') as TabName;

  const tabElements = (() => {
    const tabElements: TabElement[] = [
      { name: BEST_ODDS, type: 'Tab', dataTestId: 'best-odds-tab' },
      { name: OFFERS, type: 'Tab', dataTestId: 'offers-tab' },
      {
        content: (
          <a className={tabStyles.linkWrapper} href={projectionsUrl}>
            <span className={tabStyles.icon}>
              <Icon accent="#1ed560" className={tabStyles.projection} icon="Projections" primary="#00182e" />
            </span>
            <span className={tabStyles.text}>Stats</span>
          </a>
        ),
        name: 'Stats',
        type: 'Link',
        disabled: shouldDisableStatsTab(),
        dataTestId: 'stats-tab',
      },
    ];

    const ocTabElements: TabElement[] = [
      { name: BEST_ODDS, type: 'Tab', dataTestId: 'best-odds-tab' },
      { name: 'Match Stats', type: 'Tab', dataTestId: 'match-stats-tab' },
      { name: OFFERS, type: 'Tab', dataTestId: 'offers-tab' },
      { name: 'Tips', type: 'Tab', dataTestId: 'tips-tab' },
    ];

    const elements = repub === 'OC' ? ocTabElements : tabElements;

    return elements.filter(el => !el.disabled);
  })();

  const [selectedTab, setSelectedTab] = useState<TabElement>(tabElements[0]);
  const handleTabChange = useCallback((tabElement: TabElement) => {
    if (tabElement.type === 'Link') return;

    setSelectedTab(tabElement);
  }, []);

  return {
    tabElements,
    selectedTab,
    handleTabChange,
  };
};
